import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, doc, setDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { collectionPath } from '../../utils/firestoreCollections';
import { sanitizeString, sanitizeEmail } from '../../utils/sanitization';
import Tooltip from '../common/Tooltip';

const TRIGGER_OPTIONS = [
  { value: 'error_spike', label: 'Error Spike', description: 'Alert when error count exceeds threshold in time window' },
  { value: 'security_event', label: 'Security Event', description: 'Alert on specific security log actions (select actions below)' },
  { value: 'deletion_spike', label: 'Deletion Spike', description: 'Alert when deletion count exceeds threshold in time window' },
  { value: 'user_activity', label: 'Unusual User Activity', description: 'Alert when a single user generates too many logs in time window' },
  { value: 'email_campaign_event', label: 'Email Campaign Event', description: 'Alert on email campaign actions like failures, mass sends, or template/segment changes' },
];

const SECURITY_ACTION_OPTIONS = [
  { value: 'login_failed', label: 'Failed Login' },
  { value: 'entity_deleted', label: 'Entity Deleted' },
  { value: 'permission_denied', label: 'Permission Denied' },
  { value: 'password_reset_request', label: 'Password Reset Request' },
  { value: 'session_expired', label: 'Session Expired' },
];

const EMAIL_CAMPAIGN_ACTION_OPTIONS = [
  { value: 'campaign_sent', label: 'Campaign Sent' },
  { value: 'campaign_failed', label: 'Campaign Failed' },
  { value: 'campaign_deleted', label: 'Campaign Deleted' },
  { value: 'template_deleted', label: 'Template Deleted' },
  { value: 'segment_deleted', label: 'Segment Deleted' },
  { value: 'template_modified', label: 'Template Modified' },
  { value: 'segment_modified', label: 'Segment Modified' },
];

const PRESET_RULES = [
  {
    name: 'Brute Force Login Detection',
    triggerType: 'security_event',
    securityActions: ['login_failed'],
    threshold: 5,
    windowMinutes: 15,
    notifyLevel: 'urgent',
    notifyRecipients: 'admins',
    enabled: true,
  },
  {
    name: 'Mass Deletion Alert',
    triggerType: 'deletion_spike',
    securityActions: [],
    threshold: 10,
    windowMinutes: 30,
    notifyLevel: 'urgent',
    notifyRecipients: 'admins',
    enabled: true,
  },
  {
    name: 'Unusual User Activity',
    triggerType: 'user_activity',
    securityActions: [],
    threshold: 100,
    windowMinutes: 60,
    notifyLevel: 'warning',
    notifyRecipients: 'admins',
    enabled: true,
  },
  {
    name: 'Application Error Spike',
    triggerType: 'error_spike',
    securityActions: [],
    threshold: 15,
    windowMinutes: 30,
    notifyLevel: 'warning',
    notifyRecipients: 'admins',
    enabled: true,
  },
  {
    name: 'Campaign Send Failure',
    triggerType: 'email_campaign_event',
    securityActions: ['campaign_failed'],
    threshold: 1,
    windowMinutes: 60,
    notifyLevel: 'urgent',
    notifyRecipients: 'admins',
    enabled: true,
  },
  {
    name: 'Email Template/Segment Deletion',
    triggerType: 'email_campaign_event',
    securityActions: ['template_deleted', 'segment_deleted'],
    threshold: 1,
    windowMinutes: 60,
    notifyLevel: 'warning',
    notifyRecipients: 'admins',
    enabled: true,
  },
  {
    name: 'High Volume Campaign Activity',
    triggerType: 'email_campaign_event',
    securityActions: ['campaign_sent'],
    threshold: 5,
    windowMinutes: 60,
    notifyLevel: 'info',
    notifyRecipients: 'admins',
    enabled: true,
  },
];

const DEFAULT_RULE = {
  name: '',
  triggerType: 'error_spike',
  securityActions: [],
  threshold: 10,
  windowMinutes: 60,
  enabled: true,
  notifyLevel: 'warning',
  notifyRecipients: 'admins',
  emailEnabled: false,
  emailRecipients: [],
};

const LogAlertsTab = ({ user, isReadOnly, setToast }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [form, setForm] = useState({ ...DEFAULT_RULE });
  const [saving, setSaving] = useState(false);
  const [allUsers, setAllUsers] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch alert rules
        const snapshot = await getDocs(query(collection(db, collectionPath('logAlertRules'))));
        const loaded = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setRules(loaded);

        // Fetch users for email recipient picker
        const usersSnap = await getDocs(collection(db, collectionPath('users')));
        const users = usersSnap.docs
          .map(d => ({ uid: d.id, ...d.data() }))
          .filter(u => u.email && u.isActive !== false)
          .sort((a, b) => (a.displayName || a.email).localeCompare(b.displayName || b.email));
        setAllUsers(users);
      } catch (error) {
        console.error('Error fetching alert rules:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSave = async () => {
    if (!form.name.trim()) {
      setToast({ message: 'Alert rule name is required.', type: 'error' });
      return;
    }

    setSaving(true);
    try {
      const sanitizedName = sanitizeString(form.name, 100);
      const ruleData = {
        name: sanitizedName,
        triggerType: form.triggerType,
        securityActions: form.triggerType === 'security_event' ? (form.securityActions || []) : [],
        threshold: Math.max(1, Math.min(10000, parseInt(form.threshold) || 10)),
        windowMinutes: Math.max(5, Math.min(1440, parseInt(form.windowMinutes) || 60)),
        enabled: form.enabled,
        notifyLevel: form.notifyLevel,
        notifyRecipients: form.notifyRecipients,
        emailEnabled: form.emailEnabled || false,
        emailRecipients: form.emailEnabled ? (form.emailRecipients || []).map(e => sanitizeEmail(e)).filter(Boolean) : [],
        updatedAt: Timestamp.now(),
        updatedBy: user?.displayName || user?.email || '',
      };

      const ruleId = editingRule?.id || `alert_${sanitizedName.toLowerCase().replace(/[^a-z0-9]+/g, '_')}_${Date.now()}`;
      
      if (!editingRule) {
        ruleData.createdAt = Timestamp.now();
        ruleData.createdBy = user?.displayName || user?.email || '';
      }

      await setDoc(doc(db, collectionPath('logAlertRules'), ruleId), ruleData, { merge: true });

      if (editingRule) {
        setRules(prev => prev.map(r => r.id === ruleId ? { id: ruleId, ...ruleData } : r));
      } else {
        setRules(prev => [...prev, { id: ruleId, ...ruleData }]);
      }

      setShowForm(false);
      setEditingRule(null);
      setForm({ ...DEFAULT_RULE });
      setToast({ message: `Alert rule "${sanitizedName}" ${editingRule ? 'updated' : 'created'}.`, type: 'success' });
    } catch (error) {
      console.error('Error saving alert rule:', error);
      setToast({ message: `Failed to save alert rule: ${error.message}`, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (rule) => {
    if (isReadOnly) return;
    try {
      const newEnabled = !rule.enabled;
      await setDoc(doc(db, collectionPath('logAlertRules'), rule.id), {
        enabled: newEnabled,
        updatedAt: Timestamp.now(),
        updatedBy: user?.displayName || user?.email || '',
      }, { merge: true });
      setRules(prev => prev.map(r => r.id === rule.id ? { ...r, enabled: newEnabled } : r));
      setToast({ message: `Alert "${rule.name}" ${newEnabled ? 'enabled' : 'disabled'}.`, type: 'success' });
    } catch (error) {
      setToast({ message: `Failed to toggle alert: ${error.message}`, type: 'error' });
    }
  };

  const handleDelete = async (rule) => {
    if (isReadOnly) return;
    const confirmed = window.confirm(`Are you sure you want to delete the alert rule "${rule.name}"?`);
    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, collectionPath('logAlertRules'), rule.id));
      setRules(prev => prev.filter(r => r.id !== rule.id));
      setToast({ message: `Alert rule "${rule.name}" deleted.`, type: 'success' });
    } catch (error) {
      setToast({ message: `Failed to delete alert rule: ${error.message}`, type: 'error' });
    }
  };

  const handleEdit = (rule) => {
    setForm({
      name: rule.name || '',
      triggerType: rule.triggerType || 'error_spike',
      securityActions: rule.securityActions || [],
      threshold: rule.threshold || 10,
      windowMinutes: rule.windowMinutes || 60,
      enabled: rule.enabled !== false,
      notifyLevel: rule.notifyLevel || 'warning',
      notifyRecipients: rule.notifyRecipients || 'admins',
      emailEnabled: rule.emailEnabled || false,
      emailRecipients: rule.emailRecipients || [],
    });
    setEditingRule(rule);
    setShowForm(true);
  };

  const getTriggerLabel = (type) => {
    return TRIGGER_OPTIONS.find(t => t.value === type)?.label || type;
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center mb-4 gap-3">
        <p className="text-sm text-gray-600">
          Configure alert rules to get notified about unusual log patterns or security events.
          Rules are evaluated by scheduled Cloud Functions.
        </p>
        {!isReadOnly && (
          <Tooltip text="Create a new alert rule">
            <button
              onClick={() => {
                setForm({ ...DEFAULT_RULE });
                setEditingRule(null);
                setShowForm(true);
              }}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium whitespace-nowrap"
            >
              + New Alert Rule
            </button>
          </Tooltip>
        )}
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h4 className="font-semibold text-gray-800 mb-4">
            {editingRule ? 'Edit Alert Rule' : 'New Alert Rule'}
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Rule Name</label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. High Error Rate Alert"
                maxLength={100}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Trigger Type</label>
              <select
                value={form.triggerType}
                onChange={e => setForm(f => ({ ...f, triggerType: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                {TRIGGER_OPTIONS.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">
                {TRIGGER_OPTIONS.find(t => t.value === form.triggerType)?.description}
              </p>
            </div>
            {form.triggerType === 'security_event' && (
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-2">Security Actions to Monitor</label>
                <div className="flex flex-wrap gap-3">
                  {SECURITY_ACTION_OPTIONS.map(opt => (
                    <label key={opt.value} className="flex items-center gap-1.5 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={(form.securityActions || []).includes(opt.value)}
                        onChange={e => {
                          const current = form.securityActions || [];
                          setForm(f => ({
                            ...f,
                            securityActions: e.target.checked
                              ? [...current, opt.value]
                              : current.filter(a => a !== opt.value)
                          }));
                        }}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
                {(form.securityActions || []).length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">Select at least one action, or all security events will be monitored.</p>
                )}
              </div>
            )}
            {form.triggerType === 'email_campaign_event' && (
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-2">Email Campaign Actions to Monitor</label>
                <div className="flex flex-wrap gap-3">
                  {EMAIL_CAMPAIGN_ACTION_OPTIONS.map(opt => (
                    <label key={opt.value} className="flex items-center gap-1.5 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={(form.securityActions || []).includes(opt.value)}
                        onChange={e => {
                          const current = form.securityActions || [];
                          setForm(f => ({
                            ...f,
                            securityActions: e.target.checked
                              ? [...current, opt.value]
                              : current.filter(a => a !== opt.value)
                          }));
                        }}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
                {(form.securityActions || []).length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">Select at least one action, or all email campaign events will be monitored.</p>
                )}
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Threshold (count)</label>
              <input
                type="number"
                value={form.threshold}
                onChange={e => setForm(f => ({ ...f, threshold: e.target.value }))}
                min={1}
                max={10000}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Time Window (minutes)</label>
              <input
                type="number"
                value={form.windowMinutes}
                onChange={e => setForm(f => ({ ...f, windowMinutes: e.target.value }))}
                min={5}
                max={1440}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Notification Level</label>
              <select
                value={form.notifyLevel}
                onChange={e => setForm(f => ({ ...f, notifyLevel: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="info">Info</option>
                <option value="warning">Warning</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Notify</label>
              <select
                value={form.notifyRecipients}
                onChange={e => setForm(f => ({ ...f, notifyRecipients: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="admins">All Admins</option>
                <option value="creator">Rule Creator Only</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="rule-enabled"
                checked={form.enabled}
                onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <label htmlFor="rule-enabled" className="text-sm text-gray-700">Enabled</label>
            </div>

            {/* Email Notification */}
            <div className="sm:col-span-2 border-t border-gray-100 pt-4 mt-2">
              <div className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  id="email-enabled"
                  checked={form.emailEnabled || false}
                  onChange={e => setForm(f => ({ ...f, emailEnabled: e.target.checked }))}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <label htmlFor="email-enabled" className="text-sm font-medium text-gray-700">Also send email notifications</label>
              </div>
              {form.emailEnabled && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Email Recipients</label>
                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
                    {allUsers.length === 0 ? (
                      <p className="text-xs text-gray-400 py-2 text-center">No users available</p>
                    ) : (
                      allUsers.map(u => (
                        <label key={u.uid} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={(form.emailRecipients || []).includes(u.email)}
                            onChange={e => {
                              const current = form.emailRecipients || [];
                              setForm(f => ({
                                ...f,
                                emailRecipients: e.target.checked
                                  ? [...current, u.email]
                                  : current.filter(em => em !== u.email)
                              }));
                            }}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="text-sm text-gray-700">{u.displayName || u.email}</span>
                          {u.displayName && <span className="text-xs text-gray-400">{u.email}</span>}
                        </label>
                      ))
                    )}
                  </div>
                  {form.emailEnabled && (form.emailRecipients || []).length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">Select at least one email recipient.</p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors text-sm font-medium"
            >
              {saving ? 'Saving...' : (editingRule ? 'Update Rule' : 'Create Rule')}
            </button>
            <button
              onClick={() => { setShowForm(false); setEditingRule(null); }}
              className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Rules List */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading alert rules...</div>
      ) : rules.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <p className="text-gray-500 mb-2">No alert rules configured yet.</p>
          <p className="text-sm text-gray-400 mb-5">Create your first alert rule or start with a recommended preset below.</p>
          {!isReadOnly && (
            <div>
              <p className="text-xs font-medium text-gray-600 mb-3">Recommended Presets</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
                {PRESET_RULES.map((preset, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setForm({ ...preset });
                      setEditingRule(null);
                      setShowForm(true);
                    }}
                    className="text-left p-3 border border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
                  >
                    <p className="text-sm font-medium text-gray-800">{preset.name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {getTriggerLabel(preset.triggerType)} — {preset.threshold} in {preset.windowMinutes} min
                    </p>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {rules.map(rule => (
            <div key={rule.id} className={`bg-white rounded-lg shadow-sm border p-4 ${rule.enabled ? 'border-gray-200' : 'border-gray-100 opacity-60'}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-gray-900">{rule.name}</h4>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${rule.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {rule.enabled ? 'Active' : 'Disabled'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {getTriggerLabel(rule.triggerType)} — Threshold: <strong>{rule.threshold}</strong> in{' '}
                    <strong>{rule.windowMinutes}</strong> min — Notify: <span className="capitalize">{rule.notifyLevel}</span> to{' '}
                    <span className="capitalize">{rule.notifyRecipients}</span>
                    {rule.emailEnabled && rule.emailRecipients?.length > 0 && (
                      <span className="inline-flex items-center gap-1 ml-2 px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">
                        ✉ Email: {rule.emailRecipients.length} recipient{rule.emailRecipients.length !== 1 ? 's' : ''}
                      </span>
                    )}
                    {rule.triggerType === 'security_event' && rule.securityActions?.length > 0 && (
                      <span className="block text-xs text-gray-500 mt-0.5">
                        Actions: {rule.securityActions.map(a => SECURITY_ACTION_OPTIONS.find(o => o.value === a)?.label || a).join(', ')}
                      </span>
                    )}
                  </p>
                  {rule.updatedAt && (
                    <p className="text-xs text-gray-400 mt-1">
                      Last updated: {rule.updatedAt?.toDate ? rule.updatedAt.toDate().toLocaleString() : ''} by {rule.updatedBy || ''}
                    </p>
                  )}
                </div>
                {!isReadOnly && (
                  <div className="flex gap-2 shrink-0">
                    <Tooltip text={rule.enabled ? 'Disable this rule' : 'Enable this rule'}>
                      <button
                        onClick={() => handleToggle(rule)}
                        className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                      >
                        {rule.enabled ? '⏸ Disable' : '▶ Enable'}
                      </button>
                    </Tooltip>
                    <Tooltip text="Edit this rule">
                      <button
                        onClick={() => handleEdit(rule)}
                        className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                      >
                        ✏️ Edit
                      </button>
                    </Tooltip>
                    <Tooltip text="Delete this rule">
                      <button
                        onClick={() => handleDelete(rule)}
                        className="px-3 py-1.5 text-xs rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
                      >
                        🗑️
                      </button>
                    </Tooltip>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h4 className="font-semibold text-blue-900 mb-2">ℹ️ How Log Alerts Work</h4>
        <div className="text-sm text-blue-800 space-y-1">
          <p>Alert rules are stored in Firestore and evaluated by scheduled Cloud Functions.</p>
          <p>When a rule's threshold is exceeded, a notification is created using the CRM notification system.</p>
          <p>Admins receive notifications in-app. Configure notification preferences in your profile settings.</p>
        </div>
      </div>
    </div>
  );
};

export default LogAlertsTab;
